import React from "react";

import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useDateFormatSelect } from "store";
import { CurrencyType } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { LoanAmount } from "./form-fields/LoanAmount";
import { PaymentTerm } from "./form-fields/PaymentTerm";
import { VAT } from "./form-fields/VAT";
import { FormValues } from "./schemas";

interface Props {
  formik: FormikProps<FormValues>;
  currency: CurrencyType;
}

export const AmortizationDrawerDebtSection: React.FC<Props> = ({ formik, currency }) => {
  const { t } = useTranslation("debt");
  const dateFormat = useDateFormatSelect();

  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t("debtSection.title")}
        </Typography>
        <Divider />
      </Box>
      <Stack spacing={5}>
        <LoanAmount formik={formik} currency={currency} />
        <PaymentTerm formik={formik} />

        <Box sx={{ display: "flex", gap: 5 }}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("debtSection.dateOfIssue")}
            value={formik.values.start_date ? dayjs(formik.values.start_date) : null}
            onChange={(newValue) => formik.setFieldValue("start_date", newValue ? toIsoMidday(newValue) : "")}
            sx={{ flex: 1 }}
            slotProps={{
              textField: {
                error: formik.touched.start_date && Boolean(formik.errors.start_date),
                helperText: formik.touched.start_date && formik.errors.start_date
              }
            }}
          />
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("debtSection.maturityDate")}
            value={formik.values.maturity_date ? dayjs(formik.values.maturity_date) : null}
            onChange={(newValue) => formik.setFieldValue("maturity_date", newValue ? toIsoMidday(newValue) : "")}
            sx={{ flex: 1 }}
            slotProps={{
              textField: {
                error: formik.touched.maturity_date && Boolean(formik.errors.maturity_date),
                helperText: formik.touched.maturity_date && formik.errors.maturity_date
              }
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 5 }}>
          <DesktopDatePicker
            label="Status Date"
            format={dateFormat}
            views={["year", "month", "day"]}
            value={formik.values.loan_first_status_date ? dayjs(formik.values.loan_first_status_date) : null}
            onChange={(newValue) =>
              formik.setFieldValue("loan_first_status_date", newValue ? toIsoMidday(newValue) : "")
            }
            sx={{ flex: 1 }}
            slotProps={{
              textField: {
                error: formik.touched.loan_first_status_date && Boolean(formik.errors.loan_first_status_date),
                helperText: formik.touched.loan_first_status_date && formik.errors.loan_first_status_date
              }
            }}
          />
          <Select
            name="loan_status_frequency"
            label="Status Frequency"
            value={formik.values.loan_status_frequency}
            variant="outlined"
            items={[
              { key: "month", value: "month", text: "Monthly" },
              { key: "quarter", value: "quarter", text: "Quarterly" },
              { key: "semi-annual", value: "semi-annual", text: "Semi-Annual" },
              { key: "year", value: "year", text: "Annual" }
            ]}
            error={formik.touched.loan_status_frequency && Boolean(formik.errors.loan_status_frequency)}
            helperText={formik.touched.loan_status_frequency ? formik.errors.loan_status_frequency : undefined}
            onChange={formik.handleChange}
            formProps={{ sx: { flex: 1 } }}
          />
        </Box>

        <VAT formik={formik} />
      </Stack>
    </Box>
  );
};
